<template>
  <div class="kpi-card-content">
    <Loader
      class="loader"
      v-if="loading"
      :size="22"
      :width="2"
      color="#223448"
    />
    <div v-else class="metric-total">
      {{ metricTotal }}
    </div>
    <hr class="divider"/>
    <div class="metric-name">
      {{ customName || metricName }}
    </div>
    <div :class="metricRateChangeClasses">
      <Icon
        v-if="!isLoadingOrNoMetricRate"
        :color="metricRateChangeColor"
        small
      >
        {{ metricRateChangeIcon }}
      </Icon>
      {{ metricRateChange }}
    </div>
  </div>
</template>

<script>
import { Icon } from '@mikmak/components';
import { mapState } from 'vuex';
import {
  getDisplayValue,
  getScorecardDescriptor,
} from '../../../../services/dashboard/displayFormatService';
import utils from '../../../../services/utils';
import Loader from '../Loader.vue';
import {
  getReportFieldFromValue,
} from '../../../../services/dashboard/reportFieldService';

export default {
  components: {
    Icon,
    Loader,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
    metric: String,
    loading: Boolean,
    error: Boolean,
    hideRateChange: Boolean,
    customName: String,
  },
  data() {
    return {
      trendingUpColor: '#00A6A4',
      trendingDownColor: '#CB4444',
    };
  },
  computed: {
    ...mapState({
      allReportFields: state => state.dashboard.availableReportFields.allReportFields,
    }),
    metricReportField() {
      return getReportFieldFromValue(this.allReportFields, this.metric);
    },
    metricName() {
      return this.metricReportField.key;
    },
    metricTotal() {
      if (!this.data.total || this.error) return 'N/A';

      const displayValue = getDisplayValue(
        this.metricReportField,
        this.data.total,
        this.customFixedPlaces,
      );
      const descriptor = getScorecardDescriptor(this.metricReportField);

      return descriptor ? `${displayValue} ${descriptor}` : displayValue;
    },
    metricRateChange() {
      if (this.isLoadingOrNoMetricRate) return '-%';

      return `${utils.numberWithCommas(Math.abs(this.data.rateChange))}%`;
    },
    isLoadingOrNoMetricRate() {
      return (
        this.loading
        || this.error
        ||  this.hideRateChange
        || typeof this.data.rateChange !== 'number'
      );
    },
    customFixedPlaces() {
      switch (this.metricReportField.value) {
        case 'purchaseIntentValue':
        case 'attributableSales':
        case 'attributableUnitsSold':
        case 'avgBasketSize':
          return 0;
        case 'purchaseIntentRate':
          return 1;
        default:
          return undefined;
      }
    },
    isTrendingUp() {
      return this.data.rateChange > 0;
    },
    metricRateChangeIcon() {
      return this.isTrendingUp
        ? 'mdi-arrow-up'
        : 'mdi-arrow-down';
    },
    metricRateChangeColor() {
      return this.isTrendingUp
        ? this.trendingUpColor
        : this.trendingDownColor;
    },
    metricRateChangeClasses() {
      return {
        'metric-rate-change': true,
        'red': !this.isLoadingOrNoMetricRate && !this.isTrendingUp,
        'green': !this.isLoadingOrNoMetricRate && this.isTrendingUp,
      };
    },
  },
};

</script>

<style lang="scss" scoped>

.kpi-card-content {
  width: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.loader {
  padding-bottom: 10px;
}

.divider {
  width: 100%;
  margin-top: 5px;
  margin-bottom: 5px;
  background: #6F8CF6;
  height: 1px;
  border: none;
}

.metric-total {
  font-weight: 700;
  font-size: 30px;
  color: #223448;
}

.metric-name {
  font-weight: 500;
  text-transform: uppercase;
  font-size: 12px;
  color: #223448;
}

.metric-rate-change {
  font-weight: 700;
  font-size: 14px;
  line-height: 16px;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  margin-top: 8px;

  &.green {
    color: #00A6A4;
  }

  &.red {
    color: #CB4444,
  }
}

</style>
